// Sparkline — a tiny inline trend line with an emphasized endpoint. Powers
// StatTile trends. No axes, no labels: it's a shape, not a chart. Color
// defaults to the brand series; pass a token string to override.
//
//   <Sparkline data={[3,5,4,8,7,9,12]} />
//   <Sparkline data={pts} color="var(--good)" width={120} height={32} />

import { useId } from 'react';
import { project, linePath, areaPath } from './chartUtils';

export interface SparklineProps {
  data: number[];
  /** Internal coordinate width; also the rendered px width unless `fluid`. */
  width?: number;
  height?: number;
  /** Stretch to fill the container width (keeps `width` as the coord space). */
  fluid?: boolean;
  /** Line/endpoint color (a token string). Default brand series. */
  color?: string;
  /** Draw a faint area fill under the line. Default true. */
  area?: boolean;
  strokeWidth?: number;
  className?: string;
  ariaLabel?: string;
}

export default function Sparkline({
  data,
  width = 108,
  height = 32,
  fluid,
  color = 'var(--chart-1)',
  area = true,
  strokeWidth = 2,
  className,
  ariaLabel,
}: SparklineProps) {
  const gradId = useId().replace(/:/g, '');
  if (!data || data.length === 0) return <svg width={width} height={height} aria-hidden />;

  const pad = strokeWidth + 2;
  const box = { x: pad, y: pad, w: width - pad * 2, h: height - pad * 2 };
  const max = Math.max(...data);
  const min = Math.min(...data);
  const pts = project(data, box, max, min === max ? min - 1 : min);
  const last = pts[pts.length - 1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={['ui-chart__svg', className].filter(Boolean).join(' ')}
      style={{ width: fluid ? '100%' : width, height }}
      role="img"
      aria-label={ariaLabel ?? 'Trend'}
      preserveAspectRatio="none"
    >
      {area && (
        <>
          <defs>
            <linearGradient id={`spark-${gradId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath(pts, height - pad + 1)} fill={`url(#spark-${gradId})`} />
        </>
      )}
      <path d={linePath(pts)} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      {/* Emphasized endpoint with a surface ring so it reads over the line. */}
      <circle cx={last.x} cy={last.y} r={strokeWidth + 1.5} fill="var(--surface)" />
      <circle cx={last.x} cy={last.y} r={strokeWidth} fill={color} />
    </svg>
  );
}
